import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FolderNav, Theme } from '@orijinworks/frontend-commons';
import {
  SPLIT_MODULE_FOLDER_NAV_PROPS,
  USER_MANAGEMENT_TAB_ICONS,
} from '../split-management-folder-nav';
import { useGlobal } from 'reactn';
import { getFeatures } from '../../../util/APIUtils';
import { isPermitted, PermissionControl } from '../../../components/permission-control/permission-control';
import { ManagementService } from '../../../components/management/management.service';
import Users from '../../shared/components/users/users';
import Staff from '../../../components/management/staff';
import { Certificates } from '../management';
import { Resume } from '../management/resume';
import UnleashProvider from '../../core/unleash/unleashProvider';
import useUnleashHook from '../../core/unleash/useUnleashHook';
import { UnleashService } from '../../services';
import { useManagementSnackbar } from '../management-snackbar';
import {
  buildActiveLocation,
  useApplySelectedLocationAccess,
  useSyncManageRoute,
} from '../split-location-context';

const resumeBuilderFeature = new ManagementService().ResumeBuilder;

const UserManagement = ({
  history,
  selectedLocation,
  selectedHousingUnit,
  match,
  location,
  selectLocation,
  selectHousingUnit,
}) => {
  const [global, setGlobal] = useGlobal();
  const { isSuperAdmin, isFacilityAdmin, isTeacher, cbacConfigFlag, permissions } = global;
  const showCertificateFlag = useUnleashHook(UnleashService.FLAGS.SHOW_CERTIFICATE_IN_ADMIN_PORTAL).isFlagEnabled();
  const showResumeFlag = useUnleashHook(UnleashService.FLAGS.RESUME_MODULE).isFlagEnabled();
  const { showSnackBar, snackbar } = useManagementSnackbar();
  const [locationFeatures, setLocationFeatures] = useState([]);
  const redirectTo = useSyncManageRoute({
    match,
    location,
    history,
    selectedLocation,
    selectedHousingUnit,
    selectLocation,
    selectHousingUnit,
    setGlobal,
  });

  const activeLocation = useMemo(
    () => buildActiveLocation(selectedLocation, selectedHousingUnit),
    [selectedHousingUnit, selectedLocation]
  );
  useApplySelectedLocationAccess(redirectTo ? null : activeLocation && activeLocation.active, cbacConfigFlag, setGlobal);

  const activeLocationId = activeLocation ? activeLocation.active : null;

  useEffect(() => {
    if (activeLocationId === undefined || activeLocationId === null) {
      setLocationFeatures([]);
      return undefined;
    }

    let cancelled = false;
    getFeatures(activeLocationId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const features = response && response.statusCode === 200 && response.locationFeatures;
        setLocationFeatures(Array.isArray(features) ? features : []);
      })
      .catch(() => {
        if (!cancelled) {
          setLocationFeatures([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeLocationId]);

  const tabs = useMemo(() => {
    if (!activeLocation) {
      return [];
    }

    const canSeeDocuments = isSuperAdmin || isFacilityAdmin || isTeacher;
    const isHousingUnit = activeLocation.type === 'hu';
    const staffForceRestriction = !cbacConfigFlag && !(isSuperAdmin || isFacilityAdmin);
    const showStaff =
      !staffForceRestriction && isPermitted(cbacConfigFlag, permissions, 'Access_Staff_List');
    const resumeOn = locationFeatures.some(
      (feature) => feature.featureName === resumeBuilderFeature && feature.status
    );
    const nextTabs = [];

    if (isPermitted(cbacConfigFlag, permissions, 'View_User_List')) {
      nextTabs.push({
        label: 'Learners',
        value: 'learners',
        Icon: USER_MANAGEMENT_TAB_ICONS.learners,
        component: function LearnersTab() {
          return (
            <PermissionControl permissionName="View_User_List">
              <Users
                history={history}
                location={activeLocation.active}
                locationName={activeLocation.locationName}
                locationMeta={activeLocation}
              />
            </PermissionControl>
          );
        },
      });
    }

    if (showStaff) {
      nextTabs.push({
        label: 'Staff',
        value: 'staff',
        Icon: USER_MANAGEMENT_TAB_ICONS.staff,
        component: function StaffTab() {
          return (
            <PermissionControl forceRestriction={staffForceRestriction} permissionName="Access_Staff_List">
              <Staff
                showSnackBar={showSnackBar}
                {...{ location: activeLocation, locationFeatures }}
                history={history}
              />
            </PermissionControl>
          );
        },
      });
    }

    if (canSeeDocuments && !isHousingUnit && showCertificateFlag) {
      nextTabs.push({
        label: 'Certificates',
        value: 'certificates',
        Icon: USER_MANAGEMENT_TAB_ICONS.certificates,
        component: function CertificatesTab() {
          return (
            <UnleashProvider flagName={UnleashService.FLAGS.SHOW_CERTIFICATE_IN_ADMIN_PORTAL}>
              <Certificates locationId={activeLocation.active} />
            </UnleashProvider>
          );
        },
      });
    }

    if (canSeeDocuments && !isHousingUnit && showResumeFlag && resumeOn) {
      nextTabs.push({
        label: 'Resumes',
        value: 'resumes',
        Icon: USER_MANAGEMENT_TAB_ICONS.resumes,
        component: function ResumesTab() {
          return (
            <UnleashProvider flagName={UnleashService.FLAGS.RESUME_MODULE}>
              <Resume locationId={activeLocation.active} />
            </UnleashProvider>
          );
        },
      });
    }

    return nextTabs;
  }, [
    activeLocation,
    cbacConfigFlag,
    history,
    isFacilityAdmin,
    isSuperAdmin,
    isTeacher,
    locationFeatures,
    permissions,
    showCertificateFlag,
    showResumeFlag,
    showSnackBar,
  ]);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!activeLocation || tabs.length === 0) {
    return snackbar;
  }

  return (
    <>
      {snackbar}
      <Theme>
        <FolderNav {...SPLIT_MODULE_FOLDER_NAV_PROPS} tabs={tabs} defaultActiveTab={tabs[0].value} />
      </Theme>
    </>
  );
};

export default UserManagement;
